import {
  localStorageGetChainCode,
  localStorageGetCustomerCode,
  localStorageGetShopCode,
} from "@/common/localStorageUtil";
import { getWordList } from "@/common/graphql/expression";
import { getChainInfo } from "@/common/graphql/chain";
import { generateApolloClient } from "@/common/apolloClientUtil";
import type { ApolloQueryResult } from "@apollo/client/core";
import {
  GetTmpMemberDocument,
  type GetTmpMemberQuery,
} from "@/generated/graphql";
import deliveryRegisterInputStore from "@/store/deliveryRegisterInputStore";
import { MEMBER_REGISTER_FILE_NAME } from "../constants";
import type { UseMemberRegisterInitParams } from "../types";

/**
 * 会員登録画面の初期化処理を扱う composable。
 */
export const useMemberRegisterInit = (params: UseMemberRegisterInitParams) => {
  const {
    errdialog,
    errMessage,
    createMember,
    house_card_display,
    house_card_validation,
    house_card_required,
    formKey,
    savedTmpMemberId,
    confirmPage,
    displayCompletedDialog,
    zip_code,
    prefSelectValue,
    btnNonMemberToggle,
  } = params;

  /**
   * 初期処理
   */
  const initialize = async (): Promise<void> => {
    createMember.zip_code = "";
    createMember.address1 = "";
    createMember.address2 = "";
    createMember.address3 = "";
    createMember.chain_code = "";
    createMember.customer_code = "";
    createMember.name1 = "";
    createMember.name2 = "";
    createMember.name_kana1 = "";
    createMember.name_kana2 = "";
    createMember.phone = "";
    createMember.notes = "";
    createMember.house_card = "";

    zip_code.value = "";
    prefSelectValue.value = "";
    btnNonMemberToggle.value = 0;

    confirmPage.value = false;
    displayCompletedDialog.value = false;
  };

  /**
   * マウント時の初期ロード処理
   */
  const initOnMounted = async (wordList: Record<string, string>): Promise<void> => {
    try {
      //文言データを取得する
      Object.assign(
        wordList,
        await getWordList(
          localStorageGetChainCode(),
          MEMBER_REGISTER_FILE_NAME,
          localStorageGetCustomerCode()
        )
      );

      //初期化処理
      await initialize();

      //チェーン情報を取得する
      const result = await getChainInfo(
        localStorageGetCustomerCode(),
        localStorageGetChainCode()
      );
      if (result !== undefined && result !== null) {
        house_card_display.value = result.house_card_display
          ? result.house_card_display
          : 0;
        house_card_validation.value = result.house_card_validation
          ? result.house_card_validation
          : "";
        // ハウスカード表示ONかつバリデーション未設定の場合、デフォルトでnumericバリデーションを適用
        // （DBのCONCAT結果がNULLになるケースへの対応）
        if (
          house_card_display.value === 1 &&
          (house_card_validation.value === "" ||
            !house_card_validation.value
              .split("|")
              .some((rule: string) => rule.trim() !== ""))
        ) {
          house_card_validation.value = "numeric";
        }
        if (
          house_card_validation.value !== "" &&
          house_card_validation.value.indexOf("required") !== -1
        ) {
          house_card_required.value = 1;
        }

        /**
         * 仮会員IDから情報取得する
         */
        if (deliveryRegisterInputStore.tmp_member_id !== "") {
          try {
            //graphql接続
            const apolloClient = await generateApolloClient();

            const tmpMember: ApolloQueryResult<GetTmpMemberQuery> =
              await apolloClient.query<GetTmpMemberQuery>({
                query: GetTmpMemberDocument,
                variables: {
                  logInput: {
                    area_code: "10",
                    detail: "操作詳細",
                    display_category: "画面名",
                    operation: "操作",
                    user_id: "",
                  },
                  tmp_member_id: deliveryRegisterInputStore.tmp_member_id,
                },
                fetchPolicy: "no-cache",
              });
            const tmpMemberData = tmpMember.data.getTmpMember;
            createMember.address1 = tmpMemberData!.address1;
            createMember.address2 = tmpMemberData!.address2;
            createMember.address3 = tmpMemberData!.address3;
            createMember.name1 = tmpMemberData!.name1;
            createMember.name2 = tmpMemberData!.name2;
            createMember.name_kana1 = tmpMemberData!.name_kana1;
            createMember.name_kana2 = tmpMemberData!.name_kana2;
            createMember.phone = tmpMemberData!.phone;
            createMember.zip_code = tmpMemberData!.zip_code;

            // Form再生成
            formKey.value++;

            // クリア前に退避
            // => 仮会員の更新で使用するため
            savedTmpMemberId.value = deliveryRegisterInputStore.tmp_member_id;

            // deliveryRegisterInputStoreに保持している値をクリアする
            // => ブラウザで戻り、再度会員登録画面に開いたとき、入力値を保持させないため
            deliveryRegisterInputStore.clearValues();
          } catch (err: unknown) {
            if (err instanceof Error) {
              err.message = "仮会員情報の取得に失敗しました。";
            }
            throw err;
          }
        }
      }
    } catch (err: unknown) {
      console.error(err);
      errdialog.value = true;

      if (err instanceof Error) {
        errMessage.value = err.message;
      } else {
        errMessage.value = "不明なエラーが発生しました。";
      }

      return;
    }
  };

  return {
    initialize,
    initOnMounted,
  };
};
